import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Divider,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Chip,
    useTheme,
    Button
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SchoolIcon from '@mui/icons-material/School';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import QuizIcon from '@mui/icons-material/Quiz';
import ComputerIcon from '@mui/icons-material/Computer';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
    resetCurrentScreenForm,
    setCurrentScreenId,
    setCurrentScreenName,
    setCurrentScreenState,
    setShowAddButtons,
    resetScreenState
} from '../Slices/currentScreen';
import {
    getIndividualCourse,
    setCurrentSectionId,
    resetCourseData
} from '../Slices/currentCourse';

const OverView = () => {
    const { courseId } = useParams();
    const currentCourse = useSelector((state) => state?.currentCourse?.data);
    const shouldUpdate = useSelector((state) => state?.currentScreen?.id);
    const shouldUpdateScreen = useSelector(
        (state) => state?.currentScreen?.isLoading
    );
    const currentScreenName = useSelector(
        (state) => state?.currentScreen?.name
    );
    const currentScreenId = useSelector((state) => state?.currentScreen?.id);
    const activeSectionId = useSelector(
        (state) => state?.currentCourse?.currentSectionId
    );

    const location = useLocation();
    const dispatch = useDispatch();
    const theme = useTheme();

    const [expandedSection, setExpandedSection] = useState(null);

    // Auto-expand accordion containing the active section/lecture/exercise
    useEffect(() => {
        if (activeSectionId) {
            setExpandedSection(activeSectionId);
        }
    }, [activeSectionId]);

    const handleAccordionToggle = (sectionId) => (event, isExpanded) => {
        setExpandedSection(isExpanded ? sectionId : null);
        enableManual(sectionId);
    };

    const enableManual = (sectionId) => {
        dispatch(resetCurrentScreenForm(false));
        dispatch(setCurrentScreenName('manual'));
        dispatch(setCurrentScreenId(sectionId));
        dispatch(setCurrentSectionId(sectionId));
        dispatch(setCurrentScreenState(true));
        dispatch(setShowAddButtons(true));
    };

    const displayOverview = () => {
        dispatch(resetCurrentScreenForm(false));
        dispatch(setCurrentScreenName('overview'));
        courseId && dispatch(setCurrentScreenState(true));
        courseId && dispatch(setCurrentScreenId(courseId));
        courseId
            ? dispatch(setShowAddButtons(true))
            : dispatch(setShowAddButtons(false));
    };

    const viewLecture = (lectureid, sectionid) => {
        dispatch(resetCurrentScreenForm(false));
        dispatch(setCurrentScreenName('lesson'));
        dispatch(setCurrentScreenId(lectureid));
        dispatch(setCurrentSectionId(sectionid));
        dispatch(setCurrentScreenState(true));
        dispatch(setShowAddButtons(true));
    };

    const viewExercise = (excerciseId, sectionid) => {
        dispatch(setCurrentScreenName('exercise'));
        dispatch(setCurrentSectionId(sectionid));
        dispatch(setCurrentScreenId(excerciseId));
        dispatch(setCurrentScreenState(true));
        dispatch(setShowAddButtons(true));
    };

    const prevCourseIdRef = React.useRef(null);

    useEffect(() => {
        console.log('CourseId in edit course useEffect', courseId);
        if (prevCourseIdRef.current !== courseId) {
            dispatch(resetScreenState());
            dispatch(setCurrentSectionId(''));
            prevCourseIdRef.current = courseId;
        }

        if (courseId) {
            dispatch(getIndividualCourse(courseId));
        } else {
            dispatch(resetCourseData());
        }
    }, [shouldUpdate, currentScreenName, shouldUpdateScreen, courseId]);

    const isOverviewActive = currentScreenName === 'overview';

    return (
        <Card
            elevation={3}
            style={{
                width: '100%',
                borderRadius: '16px',
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            {/* Header Title */}
            <Box
                sx={{
                    p: 2.5,
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                }}
            >
                <SchoolIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, letterSpacing: '0.5px' }}
                >
                    Course Overview
                </Typography>
            </Box>

            <Divider />

            {/* Sidebar Content */}
            <Box
                sx={{
                    p: 2,
                    flexGrow: 1,
                    overflowY: 'auto',
                    maxHeight: 'calc(100vh - 280px)'
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mb: 1.5,
                        pl: 1,
                        fontWeight: 700,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}
                >
                    Manuals & Syllabus
                </Typography>

                {location.pathname === '/dashboard/courses/create-course' ? (
                    <Box
                        sx={{
                            p: 2,
                            border: '1px dashed',
                            borderColor: theme.palette.divider,
                            borderRadius: '8px',
                            textAlign: 'center',
                            color: 'text.secondary'
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontStyle: 'italic' }}
                        >
                            Course metadata needs to be created first
                        </Typography>
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                        {/* Course Details / Overview Button */}
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={
                                <SchoolIcon
                                    sx={{
                                        color: isOverviewActive
                                            ? theme.palette.secondary.main
                                            : theme.palette.text.secondary
                                    }}
                                />
                            }
                            onClick={displayOverview}
                            sx={{
                                justifyContent: 'flex-start',
                                px: 2.5,
                                py: 1.2,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: isOverviewActive
                                    ? theme.palette.secondary.main
                                    : theme.palette.text.primary,
                                borderColor: isOverviewActive
                                    ? theme.palette.secondary.main
                                    : theme.palette.divider,
                                backgroundColor: isOverviewActive
                                    ? 'rgba(82, 146, 221, 0.05)'
                                    : 'transparent',
                                '&:hover': {
                                    backgroundColor: isOverviewActive
                                        ? 'rgba(82, 146, 221, 0.08)'
                                        : theme.palette.action.hover,
                                    borderColor: isOverviewActive
                                        ? theme.palette.secondary.main
                                        : theme.palette.primary.main
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Course Details / Overview
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                dispatch(resetCurrentScreenForm(true));
                                dispatch(setCurrentScreenName('manual'));
                                dispatch(setCurrentScreenId(''));
                                dispatch(setCurrentScreenState(false));
                            }}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                py: 1.2,
                                borderRadius: '12px',
                                borderStyle: 'dashed',
                                borderWidth: '1.5px',
                                '&:hover': {
                                    borderStyle: 'solid',
                                    borderWidth: '1.5px',
                                    backgroundColor: 'rgba(82, 146, 221, 0.05)'
                                }
                            }}
                        >
                            Add New Manual Section
                        </Button>

                        {currentCourse?.sections?.map((section) => {
                            const isManualActive =
                                currentScreenName === 'manual' &&
                                currentScreenId === section._id;
                            const isAnyChildActive =
                                activeSectionId === section._id;

                            return (
                                <Accordion
                                    key={section._id}
                                    expanded={expandedSection === section._id}
                                    onChange={handleAccordionToggle(
                                        section._id
                                    )}
                                    elevation={isManualActive ? 2 : 0}
                                    sx={{
                                        border: `1px solid ${
                                            isManualActive
                                                ? theme.palette.secondary.main
                                                : theme.palette.divider
                                        }`,
                                        borderRadius: '12px !important',
                                        overflow: 'hidden',
                                        transition: 'all 0.2s ease',
                                        '&::before': { display: 'none' },
                                        '&:hover': {
                                            borderColor: isManualActive
                                                ? theme.palette.secondary.main
                                                : theme.palette.grey[400]
                                        }
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={
                                            <ArrowDropDownIcon
                                                sx={{
                                                    color: isManualActive
                                                        ? theme.palette
                                                              .secondary.main
                                                        : 'text.secondary'
                                                }}
                                            />
                                        }
                                        sx={{
                                            backgroundColor: isManualActive
                                                ? 'rgba(82, 146, 221, 0.05)'
                                                : 'transparent',
                                            px: 2,
                                            py: 0.5,
                                            '&:hover': {
                                                backgroundColor: isManualActive
                                                    ? 'rgba(82, 146, 221, 0.08)'
                                                    : theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            width="100%"
                                            pr={1}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1.5}
                                                sx={{ overflow: 'hidden' }}
                                            >
                                                {expandedSection ===
                                                section._id ? (
                                                    <FolderOpenIcon
                                                        sx={{
                                                            color: isManualActive
                                                                ? theme.palette
                                                                      .secondary
                                                                      .main
                                                                : theme.palette
                                                                      .primary
                                                                      .main,
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                ) : (
                                                    <FolderIcon
                                                        sx={{
                                                            color: isManualActive
                                                                ? theme.palette
                                                                      .secondary
                                                                      .main
                                                                : theme.palette
                                                                      .primary
                                                                      .main,
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                )}
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        fontWeight:
                                                            isManualActive
                                                                ? 700
                                                                : 600,
                                                        color: isManualActive
                                                            ? theme.palette
                                                                  .secondary
                                                                  .main
                                                            : theme.palette
                                                                  .primary.main,
                                                        fontSize: '0.95rem'
                                                    }}
                                                >
                                                    {section.title}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                size="small"
                                                label={
                                                    (section?.lectures
                                                        ?.length || 0) +
                                                    (section?.question
                                                        ?.length || 0) +
                                                    (section?.labs?.length || 0)
                                                }
                                                color={
                                                    isManualActive
                                                        ? 'secondary'
                                                        : 'default'
                                                }
                                                sx={{
                                                    height: '20px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    backgroundColor:
                                                        isManualActive
                                                            ? theme.palette
                                                                  .secondary
                                                                  .main
                                                            : theme.palette
                                                                  .divider,
                                                    color: isManualActive
                                                        ? '#fff'
                                                        : 'text.primary'
                                                }}
                                            />
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails
                                        sx={{
                                            p: 0,
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                            backgroundColor:
                                                theme.palette.grey[50]
                                        }}
                                    >
                                        <List disablePadding>
                                            {/* Lectures Section */}
                                            <Box
                                                sx={{
                                                    pt: 1,
                                                    pb: 0.5,
                                                    px: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between'
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: 'text.secondary',
                                                        textTransform:
                                                            'uppercase',
                                                        fontSize: '0.7rem',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Lessons
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    color="secondary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        dispatch(
                                                            resetCurrentScreenForm(
                                                                true
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentScreenName(
                                                                'lesson'
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentScreenId(
                                                                ''
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentSectionId(
                                                                section._id
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentScreenState(
                                                                false
                                                            )
                                                        );
                                                    }}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        py: 0,
                                                        px: 1,
                                                        minWidth: 0,
                                                        fontSize: '0.75rem',
                                                        borderRadius: '4px',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(82, 146, 221, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    + Add Lesson
                                                </Button>
                                            </Box>

                                            {section?.lectures?.length > 0 ? (
                                                section.lectures.map(
                                                    (lecture, index) => {
                                                        const isLectureActive =
                                                            currentScreenName ===
                                                                'lesson' &&
                                                            currentScreenId ===
                                                                lecture._id;
                                                        return (
                                                            <ListItemButton
                                                                key={
                                                                    lecture._id
                                                                }
                                                                onClick={() =>
                                                                    viewLecture(
                                                                        lecture._id,
                                                                        section._id
                                                                    )
                                                                }
                                                                sx={{
                                                                    pl: 3,
                                                                    py: 1,
                                                                    borderLeft:
                                                                        isLectureActive
                                                                            ? `4px solid ${theme.palette.secondary.main}`
                                                                            : '4px solid transparent',
                                                                    backgroundColor:
                                                                        isLectureActive
                                                                            ? 'rgba(82, 146, 221, 0.08)'
                                                                            : 'transparent',
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            isLectureActive
                                                                                ? 'rgba(82, 146, 221, 0.12)'
                                                                                : theme
                                                                                      .palette
                                                                                      .action
                                                                                      .hover
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemIcon
                                                                    sx={{
                                                                        minWidth: 28
                                                                    }}
                                                                >
                                                                    <PlayCircleOutlineIcon
                                                                        fontSize="small"
                                                                        sx={{
                                                                            color: isLectureActive
                                                                                ? theme
                                                                                      .palette
                                                                                      .secondary
                                                                                      .main
                                                                                : 'text.secondary'
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={`${
                                                                        index +
                                                                        1
                                                                    }. ${
                                                                        lecture.title
                                                                    }`}
                                                                    primaryTypographyProps={{
                                                                        sx: {
                                                                            fontWeight:
                                                                                isLectureActive
                                                                                    ? 700
                                                                                    : 500,
                                                                            color: isLectureActive
                                                                                ? theme
                                                                                      .palette
                                                                                      .secondary
                                                                                      .main
                                                                                : 'text.primary',
                                                                            fontSize:
                                                                                '0.85rem'
                                                                        }
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        );
                                                    }
                                                )
                                            ) : (
                                                <Box sx={{ py: 1, px: 3 }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.disabled"
                                                        sx={{
                                                            fontStyle: 'italic',
                                                            fontSize: '0.8rem'
                                                        }}
                                                    >
                                                        No lessons created yet
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Dynamic Lesson Creation Placeholder */}
                                            {currentScreenName === 'lesson' &&
                                                !currentScreenId &&
                                                activeSectionId ===
                                                    section._id && (
                                                    <ListItemButton
                                                        disabled
                                                        sx={{
                                                            pl: 3,
                                                            py: 1,
                                                            borderLeft: `4px dashed ${theme.palette.secondary.main}`,
                                                            backgroundColor:
                                                                'rgba(82, 146, 221, 0.03)'
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                minWidth: 28
                                                            }}
                                                        >
                                                            <PlayCircleOutlineIcon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: theme
                                                                        .palette
                                                                        .secondary
                                                                        .main,
                                                                    opacity: 0.6
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary="New Lesson (Creating...)"
                                                            primaryTypographyProps={{
                                                                sx: {
                                                                    fontWeight: 600,
                                                                    color: theme
                                                                        .palette
                                                                        .secondary
                                                                        .main,
                                                                    fontStyle:
                                                                        'italic',
                                                                    fontSize:
                                                                        '0.85rem'
                                                                }
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                )}

                                            <Divider sx={{ my: 0.5, mx: 2 }} />

                                            {/* Exercises Section */}
                                            <Box
                                                sx={{
                                                    py: 0.5,
                                                    px: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between'
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: 'text.secondary',
                                                        textTransform:
                                                            'uppercase',
                                                        fontSize: '0.7rem',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Exercises
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    color="secondary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        dispatch(
                                                            resetCurrentScreenForm(
                                                                true
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentScreenName(
                                                                'exercise'
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentScreenId(
                                                                ''
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentSectionId(
                                                                section._id
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentScreenState(
                                                                false
                                                            )
                                                        );
                                                    }}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        py: 0,
                                                        px: 1,
                                                        minWidth: 0,
                                                        fontSize: '0.75rem',
                                                        borderRadius: '4px',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(82, 146, 221, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    + Add Exercise
                                                </Button>
                                            </Box>

                                            {section?.question?.length > 0 ? (
                                                section.question.map(
                                                    (question, index) => {
                                                        const isExerciseActive =
                                                            currentScreenName ===
                                                                'exercise' &&
                                                            currentScreenId ===
                                                                question._id;
                                                        return (
                                                            <ListItemButton
                                                                key={
                                                                    question._id
                                                                }
                                                                onClick={() =>
                                                                    viewExercise(
                                                                        question._id,
                                                                        section._id
                                                                    )
                                                                }
                                                                sx={{
                                                                    pl: 3,
                                                                    py: 1,
                                                                    borderLeft:
                                                                        isExerciseActive
                                                                            ? `4px solid ${theme.palette.secondary.main}`
                                                                            : '4px solid transparent',
                                                                    backgroundColor:
                                                                        isExerciseActive
                                                                            ? 'rgba(82, 146, 221, 0.08)'
                                                                            : 'transparent',
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            isExerciseActive
                                                                                ? 'rgba(82, 146, 221, 0.12)'
                                                                                : theme
                                                                                      .palette
                                                                                      .action
                                                                                      .hover
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemIcon
                                                                    sx={{
                                                                        minWidth: 28
                                                                    }}
                                                                >
                                                                    <QuizIcon
                                                                        fontSize="small"
                                                                        sx={{
                                                                            color: isExerciseActive
                                                                                ? theme
                                                                                      .palette
                                                                                      .secondary
                                                                                      .main
                                                                                : 'text.secondary'
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={`${
                                                                        index +
                                                                        1
                                                                    }. ${
                                                                        question.question
                                                                    }`}
                                                                    primaryTypographyProps={{
                                                                        noWrap: true,
                                                                        sx: {
                                                                            fontWeight:
                                                                                isExerciseActive
                                                                                    ? 700
                                                                                    : 500,
                                                                            color: isExerciseActive
                                                                                ? theme
                                                                                      .palette
                                                                                      .secondary
                                                                                      .main
                                                                                : 'text.primary',
                                                                            fontSize:
                                                                                '0.85rem'
                                                                        }
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        );
                                                    }
                                                )
                                            ) : (
                                                <Box
                                                    sx={{
                                                        py: 1,
                                                        px: 3,
                                                        pb: 1.5
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.disabled"
                                                        sx={{
                                                            fontStyle: 'italic',
                                                            fontSize: '0.8rem'
                                                        }}
                                                    >
                                                        No exercises created yet
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Dynamic Exercise Creation Placeholder */}
                                            {currentScreenName === 'exercise' &&
                                                !currentScreenId &&
                                                activeSectionId ===
                                                    section._id && (
                                                    <ListItemButton
                                                        disabled
                                                        sx={{
                                                            pl: 3,
                                                            py: 1,
                                                            pb: 1.5,
                                                            borderLeft: `4px dashed ${theme.palette.secondary.main}`,
                                                            backgroundColor:
                                                                'rgba(82, 146, 221, 0.03)'
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                minWidth: 28
                                                            }}
                                                        >
                                                            <QuizIcon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: theme
                                                                        .palette
                                                                        .secondary
                                                                        .main,
                                                                    opacity: 0.6
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary="New Exercise (Creating...)"
                                                            primaryTypographyProps={{
                                                                sx: {
                                                                    fontWeight: 600,
                                                                    color: theme
                                                                        .palette
                                                                        .secondary
                                                                        .main,
                                                                    fontStyle:
                                                                        'italic',
                                                                    fontSize:
                                                                        '0.85rem'
                                                                }
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                )}

                                            <Divider sx={{ my: 0.5, mx: 2 }} />

                                            {/* Virtual Labs Section */}
                                            <Box
                                                sx={{
                                                    py: 0.5,
                                                    px: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between'
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: 'text.secondary',
                                                        textTransform:
                                                            'uppercase',
                                                        fontSize: '0.7rem',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Virtual Labs
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    color="secondary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        dispatch(
                                                            resetCurrentScreenForm(
                                                                true
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentScreenName(
                                                                'lab'
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentScreenId(
                                                                ''
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentSectionId(
                                                                section._id
                                                            )
                                                        );
                                                        dispatch(
                                                            setCurrentScreenState(
                                                                false
                                                            )
                                                        );
                                                    }}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        py: 0,
                                                        px: 1,
                                                        minWidth: 0,
                                                        fontSize: '0.75rem',
                                                        borderRadius: '4px',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(82, 146, 221, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    + Add Lab
                                                </Button>
                                            </Box>

                                            {section?.labs?.length > 0 ? (
                                                section.labs.map(
                                                    (lab, index) => {
                                                        const isLabActive =
                                                            currentScreenName ===
                                                                'lab' &&
                                                            currentScreenId ===
                                                                lab._id;
                                                        return (
                                                            <ListItemButton
                                                                key={lab._id}
                                                                onClick={() => {
                                                                    dispatch(
                                                                        setCurrentScreenName(
                                                                            'lab'
                                                                        )
                                                                    );
                                                                    dispatch(
                                                                        setCurrentScreenId(
                                                                            lab._id
                                                                        )
                                                                    );
                                                                    dispatch(
                                                                        setCurrentSectionId(
                                                                            section._id
                                                                        )
                                                                    );
                                                                    dispatch(
                                                                        setCurrentScreenState(
                                                                            true
                                                                        )
                                                                    );
                                                                }}
                                                                sx={{
                                                                    pl: 3,
                                                                    py: 1,
                                                                    borderLeft:
                                                                        isLabActive
                                                                            ? `4px solid ${theme.palette.secondary.main}`
                                                                            : '4px solid transparent',
                                                                    backgroundColor:
                                                                        isLabActive
                                                                            ? 'rgba(82, 146, 221, 0.08)'
                                                                            : 'transparent',
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            isLabActive
                                                                                ? 'rgba(82, 146, 221, 0.12)'
                                                                                : theme
                                                                                      .palette
                                                                                      .action
                                                                                      .hover
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemIcon
                                                                    sx={{
                                                                        minWidth: 28
                                                                    }}
                                                                >
                                                                    <ComputerIcon
                                                                        fontSize="small"
                                                                        sx={{
                                                                            color: isLabActive
                                                                                ? theme
                                                                                      .palette
                                                                                      .secondary
                                                                                      .main
                                                                                : 'text.secondary'
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={`${
                                                                        index +
                                                                        1
                                                                    }. ${
                                                                        lab.labName
                                                                    }`}
                                                                    primaryTypographyProps={{
                                                                        noWrap: true,
                                                                        sx: {
                                                                            fontWeight:
                                                                                isLabActive
                                                                                    ? 700
                                                                                    : 500,
                                                                            color: isLabActive
                                                                                ? theme
                                                                                      .palette
                                                                                      .secondary
                                                                                      .main
                                                                                : 'text.primary',
                                                                            fontSize:
                                                                                '0.85rem'
                                                                        }
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        );
                                                    }
                                                )
                                            ) : (
                                                <Box
                                                    sx={{
                                                        py: 1,
                                                        px: 3,
                                                        pb: 1.5
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.disabled"
                                                        sx={{
                                                            fontStyle: 'italic',
                                                            fontSize: '0.8rem'
                                                        }}
                                                    >
                                                        No labs created yet
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Dynamic Lab Creation Placeholder */}
                                            {currentScreenName === 'lab' &&
                                                !currentScreenId &&
                                                activeSectionId ===
                                                    section._id && (
                                                    <ListItemButton
                                                        disabled
                                                        sx={{
                                                            pl: 3,
                                                            py: 1,
                                                            pb: 1.5,
                                                            borderLeft: `4px dashed ${theme.palette.secondary.main}`,
                                                            backgroundColor:
                                                                'rgba(82, 146, 221, 0.03)'
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                minWidth: 28
                                                            }}
                                                        >
                                                            <ComputerIcon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: theme
                                                                        .palette
                                                                        .secondary
                                                                        .main,
                                                                    opacity: 0.6
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary="New Lab (Creating...)"
                                                            primaryTypographyProps={{
                                                                sx: {
                                                                    fontWeight: 600,
                                                                    color: theme
                                                                        .palette
                                                                        .secondary
                                                                        .main,
                                                                    fontStyle:
                                                                        'italic',
                                                                    fontSize:
                                                                        '0.85rem'
                                                                }
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                )}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}

                        {/* Dynamic Manual Creation Accordion Placeholder */}
                        {currentScreenName === 'manual' && !currentScreenId && (
                            <Accordion
                                disabled
                                expanded={false}
                                sx={{
                                    border: `2px dashed ${theme.palette.secondary.main}`,
                                    borderRadius: '12px !important',
                                    backgroundColor: 'rgba(82, 146, 221, 0.03)',
                                    opacity: 0.85
                                }}
                            >
                                <AccordionSummary sx={{ px: 2, py: 0.5 }}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1.5}
                                    >
                                        <FolderOpenIcon
                                            sx={{
                                                color: theme.palette.secondary
                                                    .main
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: theme.palette.secondary
                                                    .main,
                                                fontStyle: 'italic',
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            New Manual (Creating...)
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                            </Accordion>
                        )}
                    </Box>
                )}
            </Box>

            {/* Bottom Tip */}
            <Box
                sx={{
                    p: 2,
                    backgroundColor: theme.palette.grey[100],
                    borderTop: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                <InfoOutlinedIcon
                    sx={{
                        fontSize: '1.1rem',
                        color: theme.palette.text.secondary
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500
                    }}
                >
                    Click items to view or edit their contents.
                </Typography>
            </Box>
        </Card>
    );
};

export default OverView;
